import { checkEmail, type EmailValidationOptions } from '@nuxt-monorepo/ui/utils/emailValidation';

interface UseEmailFieldValidationOptions {
    email: Ref<string>;
    t: (key: string, params?: Record<string, unknown>) => string;
    externalResults?: Ref<{ email?: string } | Record<string, never>>;
    minLength?: number;
    maxLength?: number;
}

interface ValidationError {
    $message: string;
}

/**
 * Centralised email field validation with a Vuelidate-like API.
 * Handles:
 * - required / format / disposable checks (client-side)
 * - server-side errors from API
 * - typo suggestions and role-based nudges
 * - validation only on submit (not on input)
 * - non-critical warning acknowledgement (typo/role-based)
 * - configurable min/max length constraints
 */
export function useEmailFieldValidation(options: UseEmailFieldValidationOptions) {
    const { email, t, externalResults, minLength, maxLength } = options;

    // Local validation state (client-side errors and hints)
    const localErrorMessage = ref<string | null>(null);
    const suggestedEmail = ref<string | null>(null);
    const roleNudgeMessage = ref<string | null>(null);

    // Progressive validation state
    const showValidation = ref(false);
    const errorAcknowledged = ref(false);

    // Validation options for checkEmail
    const validationOptions: EmailValidationOptions = {
        minLength,
        maxLength,
    };

    /**
     * Normalised error list:
     * - first we add server-side error (if any)
     * - then local validation error
     * Order matters, because UI shows the first error.
     */
    const emailErrors = computed<ValidationError[]>(() => {
        const errors: ValidationError[] = [];

        if (externalResults?.value.email) {
            errors.push({ $message: externalResults.value.email });
        }

        if (localErrorMessage.value) {
            errors.push({ $message: localErrorMessage.value });
        }

        return errors;
    });

    const emailError = computed<string | null>(() => {
        return emailErrors.value[0]?.$message ?? null;
    });

    const canSubmit = computed<boolean>(() => {
        if (!email.value.trim()) return false;
        return emailErrors.value.length === 0;
    });

    /**
     * Check if button should be disabled.
     * Only disable if validation is shown AND there's a critical error.
     */
    const shouldDisableButton = computed<boolean>(() => {
        return showValidation.value && emailErrors.value.length > 0;
    });

    /**
     * Check if there are non-critical warnings (typo or role-based).
     */
    const hasNonCriticalWarning = computed<boolean>(() => {
        return !!(suggestedEmail.value || roleNudgeMessage.value);
    });

    const buildSuggestedEmail = (domain: string | null): string | null => {
        if (!domain) return null;

        const [localPart] = email.value.split('@');
        if (!localPart) return null;

        return `${localPart}@${domain}`;
    };

    const clearLocalState = (): void => {
        localErrorMessage.value = null;
        suggestedEmail.value = null;
        roleNudgeMessage.value = null;
    };

    /**
     * Main validation entry point.
     * Uses pure helper `checkEmail` and maps its result to:
     * - error message keys
     * - suggestion
     * - role-based nudge text
     */
    const validate = (): void => {
        const { isEmpty, isFormatValid, suggestedDomain, isRoleAddress, isDisposable } = checkEmail(
            email.value,
            validationOptions
        );

        clearLocalState();

        if (isEmpty) {
            localErrorMessage.value = t('forms.emailRequired5');
            return;
        }

        if (!isFormatValid) {
            localErrorMessage.value = t('forms.invalidEmail2');
            return;
        }

        if (isDisposable) {
            localErrorMessage.value = t('forms.disposableEmailError');
        }

        suggestedEmail.value = buildSuggestedEmail(suggestedDomain);

        if (isRoleAddress) {
            roleNudgeMessage.value = t('forms.roleEmailNudge');
        }
    };

    /**
     * Fully resets local state and clears server-side errors
     * (the latter mirrors the old `$externalResults` behaviour).
     */
    const reset = (): void => {
        clearLocalState();
        showValidation.value = false;
        errorAcknowledged.value = false;

        if (externalResults) {
            externalResults.value = {};
        }
    };

    /**
     * Input handler:
     * - clears stale server error as soon as user starts typing
     * - clears local validation state
     * - resets error acknowledgement
     */
    const onInput = (): void => {
        errorAcknowledged.value = false;

        if (externalResults?.value.email) {
            externalResults.value = {};
        }

        // Clear validation state when user types
        clearLocalState();
    };

    /**
     * Blur handler - kept for backward compatibility but not used in submit-only validation mode.
     */
    const onBlur = (): void => {
        // No validation on blur in submit-only mode
    };

    /**
     * Accepts a suggested email (did-you-mean) and re-runs validation.
     */
    const applyTypoSuggestion = (): void => {
        if (!suggestedEmail.value) return;

        email.value = suggestedEmail.value;
        // Re-validate after applying suggestion
        showValidation.value = true;
        validate();
    };

    /**
     * Bridges API/server validation to the same error pipeline
     * (so UI does not care where the error came from).
     */
    const setServerError = (message?: string | null): void => {
        if (!externalResults) return;

        externalResults.value = message ? { email: message } : {};
    };

    /**
     * Pre-submit validation check.
     * Returns true if form can be submitted, false otherwise.
     * Handles:
     * - Enabling validation display on each submit
     * - Critical error blocking
     * - Non-critical warning acknowledgement flow
     */
    const validateBeforeSubmit = (): boolean => {
        // Enable validation display
        showValidation.value = true;

        // Trigger validation on each submit
        validate();

        // If there's a critical error, block submission
        if (emailErrors.value.length > 0) {
            return false;
        }

        // If there's a non-critical warning and user hasn't acknowledged it yet
        if (hasNonCriticalWarning.value && !errorAcknowledged.value) {
            errorAcknowledged.value = true;
            return false;
        }

        // All clear - can submit
        return true;
    };

    /**
     * Call this on successful form submission to reset the form state.
     */
    const onSubmitSuccess = (): void => {
        reset();
    };

    /**
     * Vuelidate-like API, so existing components
     * can keep using `v$.email.$error`, `$errors`, `$validate()` etc.
     */
    const v$ = {
        email: {
            $model: email,
            get $error() {
                return showValidation.value && emailErrors.value.length > 0;
            },
            get $errors() {
                return showValidation.value ? emailErrors.value : [];
            },
            get $externalResults() {
                if (!externalResults?.value.email) return [];
                return [externalResults.value.email];
            },
            $reset: reset,
        },
        get $error() {
            return showValidation.value && emailErrors.value.length > 0;
        },
        $validate: validate,
        $reset: reset,
    } as const;

    // Watch email changes to reset acknowledgement and clear validation
    watch(email, () => {
        errorAcknowledged.value = false;
        // Hide validation messages when user starts typing again
        if (showValidation.value) {
            clearLocalState();
        }
    });

    return {
        v$,
        emailError,
        suggestedEmail,
        roleNudgeMessage,
        canSubmit,
        shouldDisableButton,
        showValidation,
        hasNonCriticalWarning,
        onInput,
        onBlur,
        applyTypoSuggestion,
        setServerError,
        validateBeforeSubmit,
        onSubmitSuccess,
    };
}
